import Button from "components/Button/Button";
import Card from "components/Card/Card";
import { KaboodleToaster } from "components/common";
import ControlledSelect from "components/inputs/Select/ControlledSelect";
import KaboodleModal from "components/Modals/KaboodleModal";
import { useForm } from "react-hook-form";
import {
  useCreateInvoiceLineItemMutation,
  useGenerateDefaultLineItemMutation,
} from "services/invoice-api";

import type { InvoiceType } from "./InvoiceTypes";

type PresetOption = {
  value: string | null;
  label: string;
};

type LineItemTypeSelectorProps = {
  invoice: InvoiceType;
  presetOptions: PresetOption[];
  onComplete: () => void;
  onCancel: () => void;
};

type FormValues = {
  preset: string | { value: string; label: string };
};

const LineItemTypeSelector = ({
  invoice,
  presetOptions,
  onComplete,
  onCancel,
}: LineItemTypeSelectorProps) => {
  const [createInvoiceLineItem] = useCreateInvoiceLineItemMutation();
  const [generateDefaultLineItem] = useGenerateDefaultLineItemMutation();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      preset: "custom",
    },
  });

  const handleFormSubmit = handleSubmit(async (data) => {
    try {
      // extract the value from the preset data (could be string or object)
      const presetValue =
        typeof data.preset === "string"
          ? data.preset
          : (data.preset as { value: string; label: string })?.value ||
            "custom";

      if (presetValue === "custom") {
        // create a custom line item
        await createInvoiceLineItem({
          data: {
            invoice: invoice.id,
            rate: 1,
            quantity: 1,
            description: "---",
          },
        }).unwrap();
      } else {
        // create a preset line item
        await generateDefaultLineItem({
          invoice_id: invoice.id,
          pricing_field: presetValue,
        }).unwrap();
      }

      onComplete();
    } catch (error) {
      console.error("Failed to create line item:", error);
      KaboodleToaster.error("Failed to create line item");
    }
  });

  return (
    <KaboodleModal
      isOpen={true}
      onClose={onCancel}
      footer={
        <div className="flex jc-c">
          <Button onClick={handleFormSubmit}>Create Line Item</Button>
          <Button onClick={onCancel}>Cancel</Button>
        </div>
      }
    >
      <Card heading="Select Line Item Type">
        <ControlledSelect
          control={control}
          name="preset"
          label="Type"
          options={presetOptions}
          onSave={() => Promise.resolve()} // no action on select, only on form submit
          errors={errors}
          preventClear={true}
          placeholder="Choose a type..."
        />
      </Card>
    </KaboodleModal>
  );
};

export default LineItemTypeSelector;
